/**
 * CallFlow Settings Tab Component
 *
 * Edit CallFlow configuration (name, description, retry delays).
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { callFlowService, CallFlow, UpdateCallFlowData } from '@/services/callFlowService';
import { VariablesEditor } from './VariablesEditor';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Loader2, Save } from 'lucide-react';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface CallFlowSettingsTabProps {
  organizationId: string;
  callflow: CallFlow;
  onUpdate: () => void;
}

interface FormData extends UpdateCallFlowData {
  retry_count: number;
}

export function CallFlowSettingsTab({ callflow, organizationId, onUpdate }: CallFlowSettingsTabProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [retryDelays, setRetryDelays] = useState<number[]>(callflow.retry_delays);
  const [variables, setVariables] = useState<Record<string, string>>(
    (callflow.variables as Record<string, string>) || {}
  );

  const form = useForm<FormData>({
    defaultValues: {
      name: callflow.name,
      description: callflow.description || '',
      retry_count: callflow.retry_delays.length,
      retry_delays: callflow.retry_delays,
    },
  });

  const handleRetryCountChange = (count: number) => {
    const currentDelays = [...retryDelays];
    const newDelays = Array.from({ length: count }, (_, i) => {
      if (i < currentDelays.length) {
        return currentDelays[i];
      }
      // Default values for new retry attempts
      if (i === 0) return 2;
      if (i === 1) return 10;
      return 30;
    });
    setRetryDelays(newDelays);
  };

  const updateRetryDelay = (index: number, value: number) => {
    const newDelays = [...retryDelays];
    newDelays[index] = value;
    setRetryDelays(newDelays);
  };

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);

      const updateData: UpdateCallFlowData = {
        name: data.name,
        description: data.description,
        retry_delays: retryDelays,
        variables,
      };

      await callFlowService.updateCallFlow(organizationId, callflow.id, updateData);

      toast({
        title: 'Success',
        description: 'CallFlow settings updated successfully',
      });

      onUpdate();
    } catch (error: any) {
      console.error('Failed to update CallFlow:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update CallFlow',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canEditConfig = callflow.status !== 'active';

  return (
    <div className="space-y-4">
      {!canEditConfig && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            CallFlow configuration cannot be modified while it's active. Pause the CallFlow
            first to make changes.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>CallFlow Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Name */}
              <FormField
                control={form.control}
                name="name"
                rules={{ required: 'Name is required' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={!canEditConfig} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Retry Configuration */}
              <div className="space-y-4 border rounded-lg p-4 bg-muted">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium">Retry Configuration</h3>
                </div>

                <FormField
                  control={form.control}
                  name="retry_count"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Number of Retries</FormLabel>
                      <FormControl>
                        <select
                          className="w-full border rounded p-2"
                          value={field.value}
                          onChange={(e) => {
                            const count = parseInt(e.target.value);
                            field.onChange(count);
                            handleRetryCountChange(count);
                          }}
                          disabled={!canEditConfig}
                        >
                          {[1, 2, 3, 4, 5].map((num) => (
                            <option key={num} value={num}>
                              {num} {num === 1 ? 'Retry' : 'Retries'}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormDescription>
                        Number of times to retry a call if it fails with eligible reasons
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Retry Delays */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Retry Delays (Minutes)</label>
                  <p className="text-sm text-muted-foreground">
                    Configure delay in minutes before each retry attempt
                  </p>
                  <div className="space-y-2">
                    {retryDelays.map((delay, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-sm w-24">Retry {index + 1}:</span>
                        <Input
                          type="number"
                          min="1"
                          value={delay}
                          onChange={(e) => updateRetryDelay(index, parseInt(e.target.value))}
                          className="w-32"
                          disabled={!canEditConfig}
                        />
                        <span className="text-sm text-muted-foreground">minutes</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-xs text-muted-foreground bg-secondary p-2 rounded">
                  <strong>Retry-eligible reasons:</strong> busy, no answer, customer ended
                  during ring, timeout, network connection lost, failed
                </div>
              </div>

              {/* Prompt Variables */}
              <div className="space-y-3 border rounded-lg p-4 bg-muted">
                <div>
                  <h3 className="text-sm font-medium">Prompt Variables</h3>
                  <p className="text-xs text-ink-faint mt-0.5">
                    Values for <code className="font-mono">{'{variable}'}</code> placeholders in the
                    assistant prompt. Lead fields are auto-filled per call.
                  </p>
                </div>
                <VariablesEditor
                  variables={variables}
                  onChange={setVariables}
                  disabled={!canEditConfig}
                />
              </div>

              {/* Read-only fields */}
              <div className="border-t pt-4 space-y-3">
                <h3 className="text-sm font-medium">Read-Only Settings</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Rate Limit</p>
                    <p className="font-medium">
                      {callflow.max_calls_per_second_per_number} calls/second per phone
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Max Concurrent Calls</p>
                    <p className="font-medium">{callflow.max_concurrent_calls}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Created At</p>
                    <p className="font-medium">
                      {new Date(callflow.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Last Updated</p>
                    <p className="font-medium">
                      {new Date(callflow.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={isSubmitting || !canEditConfig}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
